import { cn } from "@lib/cn";
import { useIsMobile } from "@library/shared";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { TableSortState } from "../ctx";
import {
  type ColumnFilter,
  type TableFilterState,
} from "../utils/apply-filters";
import { columnLetter } from "../utils/column-letter";
import { PortalAtTrigger } from "./portal-at-trigger";
import { SortFilterSheet } from "./sort-filter-sheet";
import { CaretDownIcon, FilterIcon, ThreeDotsIcon } from "./table-bar-icons";
import { TableFilterMenu } from "./table-filter-menu";
import { TableSortColumnPickerMenu } from "./table-sort-menu";

interface ISortPickerProps {
  tableCols: number;
  tableColWidths: number[];
  mobileColWidths: string[];
  sortState: TableSortState;
  onSortApply: (col: number, direction: "asc" | "desc" | null) => void;
  sortEnabled: boolean;
  filterEnabled: boolean;
  filterState?: TableFilterState;
  uniqueValuesByCol?: string[][];
  columnKindByCol?: ("number" | "text")[];
  onFilterChange?: (col: number, next: ColumnFilter | null) => void;
}

type PickerOpenMenu =
  | { kind: "sort"; col: number; el: HTMLElement }
  | { kind: "filter"; col: number; el: HTMLElement }
  | { kind: "sheet"; col: number; el: HTMLElement }
  | null;

export const TableSortColumnPicker = ({
  tableCols,
  tableColWidths,
  mobileColWidths,
  sortState,
  onSortApply,
  sortEnabled,
  filterEnabled,
  filterState,
  uniqueValuesByCol,
  columnKindByCol,
  onFilterChange,
}: ISortPickerProps) => {
  const isMobile = useIsMobile();
  const [openMenu, setOpenMenu] = useState<PickerOpenMenu>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!openMenu) return;
    const handleMouse = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (rootRef.current?.contains(target)) return;
      if ((target as HTMLElement | null)?.closest?.(".table-cell-portal-menu"))
        return;
      if (
        (target as HTMLElement | null)?.closest?.(
          ".table-cards-mobile-filter-sheet",
        )
      )
        return;
      setOpenMenu(null);
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenMenu(null);
    };
    document.addEventListener("mousedown", handleMouse);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleMouse);
      document.removeEventListener("keydown", handleKey);
    };
  }, [openMenu]);

  return (
    <div
      ref={rootRef}
      className="table-sort-column-picker"
      role="toolbar"
      aria-label="Sort and filter columns"
      data-menu-open={openMenu !== null ? "" : undefined}
    >
      {Array.from({ length: tableCols }, (_, col) => {
        const widthPct = isMobile
          ? mobileColWidths[col]
          : tableColWidths[col]
            ? `${tableColWidths[col]}%`
            : `${100 / tableCols}%`;
        const isActive = sortState?.col === col;
        const direction = isActive ? sortState?.direction : null;
        const sortOpen = openMenu?.kind === "sort" && openMenu.col === col;
        const filterOpen = openMenu?.kind === "filter" && openMenu.col === col;
        const sheetOpen = openMenu?.kind === "sheet" && openMenu.col === col;
        const colUniqueValues = uniqueValuesByCol?.[col] ?? [];
        const colKind = columnKindByCol?.[col] ?? "text";
        const colFilter = filterState?.get(col) ?? null;
        const filterActive =
          filterEnabled &&
          !!colFilter &&
          (colFilter.kind === "values"
            ? colFilter.selected.size < colUniqueValues.length
            : colFilter.min !== null || colFilter.max !== null);
        const mobilePickerEnabled = isMobile && (sortEnabled || filterEnabled);
        const toggleMobileSheet = (el: HTMLElement) => {
          setOpenMenu(sheetOpen ? null : { kind: "sheet", col, el });
        };
        return (
          <div
            key={col}
            className="table-sort-column-picker-cell-wrap"
            // Grow proportional to the width so cells fill exactly like the
            // table's <col> columns (which distribute extra space); otherwise
            // fixed mobile px widths leave the buttons clustered and misaligned.
            style={{
              width: widthPct,
              flexGrow: isMobile ? Number.parseFloat(String(widthPct)) || 1 : 0,
            }}
          >
            <div
              className="table-sort-column-picker-cell"
              data-active={isActive ? "" : undefined}
              data-open={sortOpen || filterOpen || sheetOpen ? "" : undefined}
              data-direction={direction ?? undefined}
              data-has-filter={filterEnabled ? "" : undefined}
              role={mobilePickerEnabled ? "button" : undefined}
              tabIndex={mobilePickerEnabled ? 0 : undefined}
              aria-haspopup={mobilePickerEnabled ? "dialog" : undefined}
              aria-expanded={mobilePickerEnabled ? sheetOpen : undefined}
              aria-label={
                mobilePickerEnabled
                  ? `Sort and filter column ${columnLetter(col)}`
                  : undefined
              }
              onClick={(e) => {
                if (!mobilePickerEnabled) return;
                toggleMobileSheet(e.currentTarget);
              }}
              onKeyDown={(e) => {
                if (!mobilePickerEnabled) return;
                if (e.key !== "Enter" && e.key !== " ") return;
                e.preventDefault();
                toggleMobileSheet(e.currentTarget);
              }}
            >
              <span className="table-sort-column-picker-letter">
                {columnLetter(col)}
              </span>
              {isMobile ? (
                (sortEnabled || filterEnabled) && (
                  <span
                    data-filter-trigger
                    className={cn(
                      "table-cell-filter-indicator tw-pointer-events-auto tw-absolute tw-right-2 tw-top-1/2 tw-z-20 tw-flex tw-h-5 tw-w-5 -tw-translate-y-1/2 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded tw-border-0 tw-bg-[transparent] tw-text-blue-500 hover:tw-bg-blue-200 focus-visible:tw-bg-blue-200",
                      isMobile && "hover:!tw-bg-[transparent]",
                    )}
                    aria-hidden
                  >
                    <ThreeDotsIcon />
                  </span>
                )
              ) : (
                <>
                  {filterEnabled && onFilterChange && (
                    <button
                      type="button"
                      className={cn(
                        "table-cell-filter-indicator tw-pointer-events-auto tw-absolute tw-top-1/2 tw-z-20 tw-flex tw-h-5 tw-w-5 -tw-translate-y-1/2 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded tw-border-0 tw-bg-[transparent] tw-text-blue-500 hover:tw-bg-blue-200 focus-visible:tw-bg-blue-200",
                        sortEnabled ? "tw-right-8" : "tw-right-2",
                      )}
                      data-active={filterActive ? "" : undefined}
                      data-open={filterOpen ? "" : undefined}
                      aria-haspopup="dialog"
                      aria-expanded={filterOpen}
                      aria-label={`Filter column ${columnLetter(col)}`}
                      onClick={(e) =>
                        setOpenMenu(
                          filterOpen
                            ? null
                            : { kind: "filter", col, el: e.currentTarget },
                        )
                      }
                    >
                      <FilterIcon />
                    </button>
                  )}
                  {sortEnabled && (
                    <button
                      type="button"
                      className="table-sort-column-picker-caret tw-pointer-events-auto tw-absolute tw-right-2 tw-top-1/2 tw-z-10 tw-flex tw-h-5 tw-w-5 -tw-translate-y-1/2 tw-cursor-pointer tw-items-center tw-justify-center tw-rounded tw-border-0 tw-bg-[transparent] tw-text-blue-500 hover:tw-bg-blue-200 focus-visible:tw-bg-blue-200"
                      data-open={sortOpen ? "" : undefined}
                      aria-haspopup="menu"
                      aria-expanded={sortOpen}
                      aria-label={`Sort column ${columnLetter(col)}`}
                      onClick={(e) =>
                        setOpenMenu(
                          sortOpen
                            ? null
                            : { kind: "sort", col, el: e.currentTarget },
                        )
                      }
                    >
                      <CaretDownIcon />
                    </button>
                  )}
                </>
              )}
            </div>
            {!isMobile && sortEnabled && sortOpen && (
              <PortalAtTrigger trigger={openMenu?.el ?? null}>
                <TableSortColumnPickerMenu
                  activeDirection={direction}
                  onApply={(d) => {
                    onSortApply(col, d);
                    setOpenMenu(null);
                  }}
                />
              </PortalAtTrigger>
            )}
            {!isMobile && filterOpen && onFilterChange && (
              <PortalAtTrigger trigger={openMenu?.el ?? null}>
                <TableFilterMenu
                  columnKind={colKind}
                  uniqueValues={colUniqueValues}
                  filter={colFilter}
                  onChange={(next) => onFilterChange(col, next)}
                />
              </PortalAtTrigger>
            )}
            {isMobile &&
              sheetOpen &&
              createPortal(
                <>
                  <div
                    aria-hidden
                    className="tw-fixed tw-inset-0 tw-bg-black/30"
                    style={{ zIndex: 9998 }}
                  />
                  <div
                    style={{
                      position: "fixed",
                      left: 8,
                      right: 8,
                      bottom: 8,
                      zIndex: 9999,
                    }}
                  >
                    <SortFilterSheet
                      sortEnabled={sortEnabled}
                      filterEnabled={filterEnabled}
                      columnKind={colKind}
                      values={colUniqueValues}
                      initialSort={direction}
                      initialFilter={colFilter}
                      onApply={(sort, filter) => {
                        onSortApply(col, sort);
                        onFilterChange?.(col, filter);
                        setOpenMenu(null);
                      }}
                      onReset={() => {
                        onSortApply(col, null);
                        onFilterChange?.(col, null);
                        setOpenMenu(null);
                      }}
                      onClose={() => setOpenMenu(null)}
                    />
                  </div>
                </>,
                document.querySelector(".sd-presentation") ?? document.body,
              )}
          </div>
        );
      })}
    </div>
  );
};
